import { useState, useEffect } from 'react'
import { CalculatorData } from '../types'

interface UseCalculatorProps {
  initialCapital: number
  monthlyContribution: number
  years: number
  cagr: number
}

export const useCalculator = ({ initialCapital, monthlyContribution, years, cagr }: UseCalculatorProps) => {
  const [calculatorData, setCalculatorData] = useState<CalculatorData[]>([])
  const [finalValue, setFinalValue] = useState(0)

  useEffect(() => {
    if (initialCapital < 0 || monthlyContribution < 0 || years <= 0 || cagr < 0 || (initialCapital === 0 && monthlyContribution === 0)) {
      setCalculatorData([])
      setFinalValue(0)
      return
    }

    const data: CalculatorData[] = []
    const annualRate = cagr / 100

    // Financial functions - simplified implementations
    const fv = (rate: number, nper: number, pmt: number, pv: number) => {
      if (rate === 0) {
        return -(pv + pmt * nper)
      }
      const factor = Math.pow(1 + rate, nper)
      return -(pv * factor + pmt * (factor - 1) / rate)
    }

    // Add initial year
    data.push({
      year: 0,
      value: initialCapital,
      growth: 0,
      totalGrowth: 0,
      contributions: 0,
      totalContributions: 0
    })

    // Calculate year by year
    let totalContributions = 0
    for (let year = 1; year <= years; year++) {
      // Calculate future value of initial capital
      const initialFV = Math.abs(fv(annualRate, year, 0, -initialCapital))
      
      // Calculate future value of monthly contributions (annuity)
      const contributionsFV = monthlyContribution > 0 
        ? Math.abs(fv(annualRate / 12, year * 12, -monthlyContribution, 0))
        : 0
        
      const currentValue = initialFV + contributionsFV
      
      // Calculate growth for this specific year
      const previousValue = year === 1 
        ? initialCapital 
        : Math.abs(fv(annualRate, year - 1, 0, -initialCapital)) + 
          (monthlyContribution > 0 ? Math.abs(fv(annualRate / 12, (year - 1) * 12, -monthlyContribution, 0)) : 0)
          
      const yearContributions = monthlyContribution * 12
      totalContributions += yearContributions
      const growth = currentValue - previousValue - yearContributions
      const totalGrowth = currentValue - initialCapital - totalContributions

      data.push({
        year: year,
        value: currentValue,
        growth: growth,
        totalGrowth: totalGrowth,
        contributions: yearContributions,
        totalContributions: totalContributions
      })
    }

    // Calculate final value
    const initialFV = Math.abs(fv(annualRate, years, 0, -initialCapital))
    const contributionsFV = monthlyContribution > 0 
      ? Math.abs(fv(annualRate / 12, years * 12, -monthlyContribution, 0))
      : 0
    const final = initialFV + contributionsFV

    setCalculatorData(data)
    setFinalValue(final)
  }, [initialCapital, monthlyContribution, years, cagr])

  return { calculatorData, finalValue }
}